import React, {useEffect, useState} from 'react';
import {useAuth} from '../../context/AuthContext';
import {useNavigate} from 'react-router-dom';
import dayjs from 'dayjs';
import apiClient from '../../context/apiClient';
import User from "../../assets/icons/User.svg";
import Coffee from "../../assets/icons/Clipboard.svg";
import Tool from "../../assets/icons/Tool.svg";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

function formatForInput(date) {
    const pad = (n) => n.toString().padStart(2, '0');
    const yyyy = date.getFullYear();
    const MM = pad(date.getMonth() + 1);
    const dd = pad(date.getDate());
    const hh = pad(date.getHours());
    const mm = pad(date.getMinutes());
    return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
}

const colorMap = {
    MECHANIC: {bg: 'bg-emerald-200', text: 'text-emerald-900'},
    GUEST_MANAGER: {bg: 'bg-rose-200', text: 'text-rose-900'},
    INSTRUCTOR: {bg: 'bg-indigo-100', text: 'text-indigo-800'},
};

const getScoreColor = (score) => {
    if (score === null || score === undefined || !isFinite(score)) return 'text-gray-400';

    if (score < 0.4) return 'text-red-600';
    if (score < 0.8) return 'text-red-400';
    if (score < 1.0) return 'text-orange-300';

    if (score < 1.1) return 'text-green-400';
    if (score < 1.2) return 'text-green-500';
    if (score < 1.4) return 'text-green-600';
    if (score < 1.6) return 'text-emerald-600';
    return 'text-emerald-700';
};


const Dashboard = () => {
    const {user} = useAuth();
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hoveredEmployee, setHoveredEmployee] = useState(null);
    const currentYear = new Date().getFullYear();
    const lastYear = currentYear - 1;
    const [fromDate, setFromDate] = useState(() => {
        const now = new Date();
        const from = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0); // 1. Tag des Vormonats, 00:00
        return from;
    });

    const [toDate, setToDate] = useState(() => {
        const now = new Date();
        const to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59); // Letzter Tag des Vormonats, 23:59
        return to;
    });


    const [employeeScores, setEmployeeScores] = useState([]);
    const [loadingScores, setLoadingScores] = useState(false);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        // Load initial dashboard data without date parameters or with default dates
        loadDashboardData();
    }, [user, navigate]);

    useEffect(() => {
        if (user) {
            fetchEmployeeScores();
        }
    }, [user]);

    const loadDashboardData = async () => {
        try {
            const response = await apiClient.get('/api/utility/dashboardData', {
                params: {
                    from: dayjs(fromDate).format('YYYY-MM-DDTHH:mm:ss'),
                    to: dayjs(toDate).format('YYYY-MM-DDTHH:mm:ss'),
                }
            });
            setDashboardData(response.data);
            setLoading(false);
        } catch (error) {
            console.error("Fehler beim Laden der Dashboard-Daten:", error);
            setError('Fehler beim Laden der Dashboard-Daten');
            setLoading(false);
        }
    };

    const fetchEmployeeScores = async () => {
        setLoadingScores(true);
        try {
            const res = await apiClient.get('/api/utility/dashboardEmployeeData', {
                params: {
                    from: dayjs(fromDate).format('YYYY-MM-DDTHH:mm:ss'),
                    to: dayjs(toDate).format('YYYY-MM-DDTHH:mm:ss'),
                }
            });

            setEmployeeScores(res.data);
        } catch (err) {
            console.error('Fehler beim Abrufen der Mitarbeiterscores:', err);
        } finally {
            setLoadingScores(false);
        }
    };

    const handleLoadData = () => {
        loadDashboardData();
        fetchEmployeeScores();
    };

    const formatEfficiencyScore = (val) => {
        if (typeof val !== 'number' || !isFinite(val)) return '–';
        return val.toFixed(2);
    };


    const groupedByRole = ['INSTRUCTOR', 'MECHANIC', 'GUEST_MANAGER'].map(role => {
        const valid = employeeScores
            .filter(e => e.role === role && typeof e.efficiencyScore === 'number' && isFinite(e.efficiencyScore))
            .sort((a, b) => b.efficiencyScore - a.efficiencyScore);

        const invalid = employeeScores
            .filter(e => e.role === role && (!isFinite(e.efficiencyScore) || typeof e.efficiencyScore !== 'number'));

        return {
            role,
            employees: [...valid, ...invalid]
        };
    });


    const formatValue = (val) => {
        if (val === null) return "Stundenkontingente nicht vorhanden";
        if (val === Infinity || val === -Infinity) return "Leistungspakete Upload nicht vorhanden";
        return val.toLocaleString('de-DE', {minimumFractionDigits: 2});
    };

    const getTrendSafe = (current, last) => {
        if (current === null || last === null || last === 0) return 'Nicht verfügbar';
        const diff = ((current - last) / last) * 100;
        const sign = diff >= 0 ? '+' : '';
        return `${sign}${diff.toFixed(2)}%`;
    };

    const getCardColorClass = (current, last) => {
        if (current === null || last === null || last === 0) return 'bg-yellow-100 border-yellow-400';
        return current >= last ? 'bg-green-100 border-green-400' : 'bg-red-100 border-red-400';
    };


    if (error) {
        return (
            <div className="text-red-500 text-center mt-10">
                {error}
            </div>
        );
    }

    return (
        <main className="fixed top-16 inset-0 bg-gray-100 overflow-y-auto overflow-x-auto">
            <div className="px-4 py-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    {/* Umsatzvergleich Card */}
                    <div className="bg-white p-6 rounded-xl shadow-md col-span-1 flex flex-col justify-between">
                        <h2 className="text-base font-semibold text-gray-800 mb-2">
                            Umsatz {dayjs(fromDate).format('DD.MM.YYYY')} - {dayjs(toDate).format('DD.MM.YYYY')}
                        </h2>
                        <p className="text-2xl font-bold">
                            {dashboardData ? `${formatValue(dashboardData.totalCurrentYear)} €` : <Skeleton width={80}/>}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                            Trend: {dashboardData ? getTrendSafe(dashboardData.totalCurrentYear, dashboardData.totalLastYear) :
                            <Skeleton width={60}/>}
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                            Vergleich: {dashboardData ? `${formatValue(dashboardData.totalLastYear)} (${lastYear})` :
                            <Skeleton width={100}/>}
                        </p>
                    </div>

                    {/* Instruktoren */}
                    <div className="bg-blue-100 p-6 rounded-xl shadow-md flex flex-col justify-between">
                        <div className="flex items-center gap-2 mb-3">
                            <img src={User} alt="User Icon" className="w-8 h-8"/>
                            <span className="font-medium">Instruktoren</span>
                        </div>
                        <div className="space-y-2">
                            <div>
                                <div className="text-blue-800 text-2xl font-bold">
                                    {dashboardData ? dashboardData.efficiencyInstructorsCurrent?.toFixed(2) : <Skeleton width={40}/>}
                                </div>
                                <div className="text-gray-700 text-sm">Effektivität</div>
                            </div>
                            <div>
                                <div className="text-blue-600 text-xl font-semibold">
                                    {dashboardData ? dashboardData.hoursInstructorsCurrent?.toFixed(1) + ' Std' : <Skeleton width={50}/>}
                                </div>
                                <div className="text-gray-700 text-sm">Stundenabweichung</div>
                            </div>
                        </div>
                        <div className="text-blue-600 text-sm mt-2">
                            {dashboardData
                                ? `${getTrendSafe(dashboardData.efficiencyInstructorsCurrent, dashboardData.efficiencyInstructorsLast)} zum Vorjahr`
                                : <Skeleton width={80}/>}
                        </div>
                    </div>

                    {/* Mechaniker */}
                    <div className="bg-green-100 p-6 rounded-xl shadow-md flex flex-col justify-between">
                        <div className="flex items-center gap-2 mb-3">
                            <img src={Tool} alt="Tool Icon" className="w-8 h-8"/>
                            <span className="font-medium">Mechaniker</span>
                        </div>
                        <div className="space-y-2">
                            <div>
                                <div className="text-green-800 text-2xl font-bold">
                                    {dashboardData ? dashboardData.efficiencyMechanicsCurrent?.toFixed(2) : <Skeleton width={40}/>}
                                </div>
                                <div className="text-gray-700 text-sm">Effektivität</div>
                            </div>
                            <div>
                                <div className="text-green-600 text-xl font-semibold">
                                    {dashboardData ? dashboardData.hoursMechanicsCurrent?.toFixed(1) + ' Std' : <Skeleton width={50}/>}
                                </div>
                                <div className="text-gray-700 text-sm">Stundenabweichung</div>
                            </div>
                        </div>
                        <div className="text-green-600 text-sm mt-2">
                            {dashboardData
                                ? `${getTrendSafe(dashboardData.efficiencyMechanicsCurrent, dashboardData.efficiencyMechanicsLast)} zum Vorjahr`
                                : <Skeleton width={80}/>}
                        </div>
                    </div>

                    {/* Guest Manager */}
                    <div className="bg-red-100 p-6 rounded-xl shadow-md flex flex-col justify-between">
                        <div className="flex items-center gap-2 mb-3">
                            <img src={Coffee} alt="Coffee Icon" className="w-8 h-8"/>
                            <span className="font-medium">Guestmanager</span>
                        </div>
                        <div className="space-y-2">
                            <div>
                                <div className="text-red-800 text-2xl font-bold">
                                    {dashboardData ? dashboardData.efficiencyGuestManagersCurrent?.toFixed(2) : <Skeleton width={40}/>}
                                </div>
                                <div className="text-gray-700 text-sm">Effektivität</div>
                            </div>
                            <div>
                                <div className="text-red-600 text-xl font-semibold">
                                    {dashboardData ? dashboardData.hoursGuestManagersCurrent?.toFixed(1) + ' Std' : <Skeleton width={50}/>}
                                </div>
                                <div className="text-gray-700 text-sm">Stundenabweichung</div>
                            </div>
                        </div>
                        <div className="text-red-600 text-sm mt-2">
                            {dashboardData
                                ? `${getTrendSafe(dashboardData.efficiencyGuestManagersCurrent, dashboardData.efficiencyGuestManagersLast)} zum Vorjahr`
                                : <Skeleton width={80}/>}
                        </div>
                    </div>
                </div>
            </div>


            <div className="grid grid-cols-4 gap-3 px-4 sm:px-6 lg:px-8" style={{height: 'calc(100vh - 340px)'}}>
                {/* 1. Datepicker */}
                <div className="bg-white p-4 rounded-xl shadow-md overflow-y-auto flex flex-col">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4 text-center">Zeitraum für Effizienzwertung
                        wählen</h3>
                    <div className="flex flex-col gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-700">Von</label>
                            <input
                                type="datetime-local"
                                value={formatForInput(fromDate)}
                                onChange={e => setFromDate(new Date(e.target.value))}
                                className="border rounded px-3 py-2 w-full"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700">Bis</label>
                            <input
                                type="datetime-local"
                                value={formatForInput(toDate)}
                                onChange={e => setToDate(new Date(e.target.value))}
                                className="border rounded px-3 py-2 w-full"
                            />
                        </div>
                        <button
                            className={`mt-2 px-4 py-2 ${loadingScores ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded transition`}
                            onClick={handleLoadData}
                            disabled={loadingScores}
                            type="button"
                        >
                            {loadingScores ? 'Lädt...' : 'Lade Daten'}
                        </button>
                    </div>
                </div>

                {/* 2–4: Tabellen für Rollen */}
                {['INSTRUCTOR', 'MECHANIC', 'GUEST_MANAGER'].map(role => {
                    const roleMap = {
                        INSTRUCTOR: 'Instruktoren',
                        MECHANIC: 'Mechaniker',
                        GUEST_MANAGER: 'Guestmanager'
                    };
                    const employees = groupedByRole.find(g => g.role === role)?.employees ?? [];

                    return (
                        <div key={role} className="bg-white p-4 rounded-xl shadow-md overflow-y-auto flex flex-col">
                            <div className="overflow-y-auto" style={{flex: 1}}>
                                <div className="space-y-2">
                                    {employees.map((emp, i) => (
                                        <div className="relative w-full">
                                            {/* Colored pill bar on the left */}
                                            <div
                                                className={`absolute top-0 left-0 h-[98%] w-5 rounded-l-full pointer-events-none ${colorMap[role]?.bg}`}
                                            ></div>

                                            {/* Main pill row content */}
                                            <div
                                                className="flex items-center justify-between w-full bg-white border border-gray-200 rounded-full overflow-hidden shadow-sm pl-6 pr-4 py-1 text-sm text-gray-800 z-10">
                                                <span className="truncate">{emp.firstName} {emp.lastName}</span>
                                                <span
                                                    className={`${getScoreColor(emp.efficiencyScore)} font-medium cursor-pointer relative group`}
                                                    onMouseEnter={() => setHoveredEmployee(emp.id)}
                                                    onMouseLeave={() => setHoveredEmployee(null)}
                                                >
                                                <span
                                                    className={hoveredEmployee === emp.id ? 'opacity-0' : 'opacity-100'}>
                                                    {formatEfficiencyScore(emp.efficiencyScore)}
                                                </span>
                                                    <span
                                                        className={`absolute top-1/2 right-0 -translate-y-1/2 text-xs whitespace-nowrap ${hoveredEmployee === emp.id ? 'opacity-100' : 'opacity-0'} transition-opacity duration-200`}>
                                                        {emp.absoluteHoursDifference != null ? `${emp.absoluteHoursDifference > 0 ? '+' : ''}${emp.absoluteHoursDifference.toFixed(1)} Std Abw.` : '–'}
                                                    </span>
                                            </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </main>
    );
};

export default Dashboard;
